import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { PaperButtonComponent } from '@ricoh-paper/angular';
import { definePaperAskWidget, type PaperAskWidgetComponent } from '@ricoh-paper/angular/ask';

/**
 * The policy draft Ask made (gemba's `policy-draft` skill, kind
 * `policyDraft@1`): the place, what the draft changes in the Policies page's
 * words, its state, and a way to open it in the page's review. It never
 * applies anything: Save as draft and Apply are on the Policies page
 * (COMMON rulings 18, 22).
 */
export interface PolicyDraftPayload {
  readonly title: string;
  readonly placeLabel: string;
  readonly changes: readonly { readonly label: string; readonly value: string }[];
  readonly moreChanges: number;
  readonly note: string;
  readonly stateLabel: string | null;
  /** The Policies route and the query that opens it on the draft. */
  readonly open: { readonly label: string; readonly route: string; readonly query: Readonly<Record<string, string>> };
}

const SAFE_VALUE = /^[A-Za-z0-9_-]{1,64}$/;

/** The card's payload as Settings trusts it, or null to fall back to the sentence. */
export function parsePolicyDraft(raw: unknown): PolicyDraftPayload | null {
  if (!raw || typeof raw !== 'object') return null;
  const v = raw as Record<string, unknown>;
  const place = (v['place'] ?? {}) as Record<string, unknown>;
  const open = (v['open'] ?? {}) as Record<string, unknown>;
  const route = typeof open['route'] === 'string' ? open['route'] : '';
  // Only the Policies page: an answer cannot send the admin anywhere else from here.
  if (route !== '/governance/policies') return null;
  const query: Record<string, string> = {};
  const rawQuery = (open['query'] ?? {}) as Record<string, unknown>;
  for (const name of ['scopeId', 'draft']) {
    const value = rawQuery[name];
    if (typeof value === 'string' && SAFE_VALUE.test(value)) query[name] = value;
  }
  if (!query['draft']) return null;
  const changes = Array.isArray(v['changes'])
    ? (v['changes'] as unknown[])
        .map((c) => (c && typeof c === 'object' ? (c as Record<string, unknown>) : {}))
        .filter((c) => typeof c['label'] === 'string' && typeof c['value'] === 'string')
        .map((c) => ({ label: String(c['label']), value: String(c['value']) }))
        .slice(0, 8)
    : [];
  return {
    title: typeof v['title'] === 'string' ? v['title'] : '',
    placeLabel: typeof place['label'] === 'string' ? place['label'] : typeof place['name'] === 'string' ? place['name'] : '',
    changes,
    moreChanges: typeof v['moreChanges'] === 'number' && v['moreChanges'] > 0 ? v['moreChanges'] : 0,
    note: typeof v['note'] === 'string' ? v['note'] : '',
    stateLabel: typeof v['stateLabel'] === 'string' ? v['stateLabel'] : null,
    open: { label: typeof open['label'] === 'string' ? open['label'] : '', route, query },
  };
}

@Component({
  selector: 'ws-ask-policy-draft',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoPipe, PaperButtonComponent],
  template: `
    <section class="pd" data-testid="ask-policy-draft" [attr.aria-label]="heading()">
      <p class="pd__title">{{ heading() }}</p>
      @if (payload().placeLabel) {
        <p class="pd__place">
          {{ payload().placeLabel }}
          @if (payload().stateLabel; as state) {
            <span class="pd__state">· {{ state }}</span>
          }
        </p>
      }
      @if (payload().changes.length > 0) {
        <dl class="pd__changes">
          @for (change of payload().changes; track $index) {
            <div class="pd__row">
              <dt>{{ change.label }}</dt>
              <dd>{{ change.value }}</dd>
            </div>
          }
        </dl>
        @if (payload().moreChanges > 0) {
          <p class="pd__more">{{ 'settings_ask.policy_draft.more' | transloco: { count: payload().moreChanges } }}</p>
        }
      }
      <p class="pd__note">{{ payload().note || ('settings_ask.policy_draft.note' | transloco) }}</p>
      <paper-button
        data-testid="ask-policy-draft-open"
        intent="secondary"
        size="sm"
        icon="arrow-right"
        iconPosition="right"
        (click)="openDraft()"
      >
        {{ payload().open.label || ('settings_ask.policy_draft.open' | transloco) }}
      </paper-button>
    </section>
  `,
  styles: `
    /* A wash of the answer's surface, never an outlined box (ruling 21). */
    .pd {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: var(--s-2);
      padding: var(--s-3) var(--s-4);
      border-radius: var(--radius-md);
      background: var(--surface-sunken);
    }
    .pd__title {
      margin: 0;
      font-size: 13px;
      font-weight: 600;
      color: var(--text-primary);
    }
    .pd__place,
    .pd__more,
    .pd__note {
      margin: 0;
      font-size: 12.5px;
      line-height: 1.45;
      color: var(--text-secondary);
    }
    .pd__state {
      color: var(--text-tertiary);
    }
    .pd__changes {
      display: flex;
      flex-direction: column;
      gap: var(--s-1);
      margin: 0;
      width: 100%;
    }
    .pd__row {
      display: flex;
      justify-content: space-between;
      gap: var(--s-3);
      font-size: 13px;
    }
    .pd__row dt {
      color: var(--text-secondary);
    }
    .pd__row dd {
      margin: 0;
      font-weight: 600;
      color: var(--text-primary);
      text-align: right;
    }
  `,
})
export class PolicyDraftWidgetComponent implements PaperAskWidgetComponent<PolicyDraftPayload> {
  readonly payload = input.required<PolicyDraftPayload>();
  private readonly router = inject(Router);

  protected readonly heading = computed(() => this.payload().title || 'Policy draft to review');

  /** Opens the Policies page at the draft's place, with the draft loaded in its review. */
  protected openDraft(): void {
    const { route, query } = this.payload().open;
    void this.router.navigate([route], { queryParams: query });
  }
}

/** gemba's `policyDraft@1`: the card a policy drafted from words comes back as. */
export const SETTINGS_ASK_POLICY_DRAFT_WIDGET = definePaperAskWidget<PolicyDraftPayload>({
  kind: 'policyDraft',
  v: 1,
  parse: parsePolicyDraft,
  component: PolicyDraftWidgetComponent,
});
